import axios from 'axios';

/** Bare hostnames (no scheme) resolve relative to the admin origin and break requests. */
function resolveCreedApiBaseUrl(raw: string | undefined): string {
  const u = (raw ?? '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u.replace(/\/+$/, '');
  const bare = u.replace(/^\/+/, '').replace(/\/+$/, '');
  const local =
    /^localhost\b/i.test(bare) ||
    /^127\.0\.0\.1\b/.test(bare) ||
    /^0\.0\.0\.0\b/.test(bare);
  return `${local ? 'http' : 'https'}://${bare}`;
}

export const creedApiBaseUrl = resolveCreedApiBaseUrl(import.meta.env.VITE_API_URL);

const base = axios.create({ baseURL: creedApiBaseUrl });

// Inject Auth0 access token on every request
let getTokenFn: (() => Promise<string>) | null = null;

export function setTokenProvider(fn: () => Promise<string>) {
  getTokenFn = fn;
}

export async function getAuthToken(): Promise<string | null> {
  if (!getTokenFn) return null;
  return getTokenFn();
}

base.interceptors.request.use(async (config) => {
  if (getTokenFn) {
    const token = await getTokenFn();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const apiBase = base;

// ─── OpenAPI spec ────────────────────────────────────────────────────────────

export const openApiApi = {
  spec: (): Promise<Record<string, unknown>> =>
    base.get('/admin/openapi.json').then((r) => r.data),
};

// ─── API Keys ────────────────────────────────────────────────────────────────

export interface ApiKey {
  _id: string;
  label: string;
  keyPrefix: string;
  active: boolean;
  requestCount: number;
  lastUsedAt?: string;
  createdAt: string;
}

export interface CreatedKey extends ApiKey {
  rawKey: string;
  note: string;
}

export interface RotatedKey {
  id: string;
  label: string;
  keyPrefix: string;
  rawKey: string;
  rotatedAt: string;
  note: string;
}

export interface KeyUsage {
  key: ApiKey;
  byEntity: Array<{ _id: string; requests: number }>;
  byState: Array<{ _id: string; requests: number }>;
  recent: Assessment[];
}

export const keysApi = {
  list: (): Promise<ApiKey[]> => base.get('/admin/keys').then((r) => r.data),
  create: (label: string): Promise<CreatedKey> =>
    base.post('/admin/keys', { label }).then((r) => r.data),
  update: (id: string, patch: { label?: string; active?: boolean }): Promise<ApiKey> =>
    base.patch(`/admin/keys/${id}`, patch).then((r) => r.data),
  remove: (id: string): Promise<void> => base.delete(`/admin/keys/${id}`).then(() => undefined),
  rotate: (id: string): Promise<RotatedKey> =>
    base.post(`/admin/keys/${id}/rotate`).then((r) => r.data),
  revoke: (id: string): Promise<ApiKey> =>
    base.post(`/admin/keys/${id}/revoke`).then((r) => r.data),
  usage: (id: string): Promise<KeyUsage> =>
    base.get(`/admin/keys/${id}/usage`).then((r) => r.data),
};

// ─── Assessments (logged) ────────────────────────────────────────────────────

export interface Assessment {
  _id: string;
  requestedAt: string;
  metadata: { apiKeyId: string; apiKeyLabel: string };
  params: {
    entity: string;
    states: string[];
    fundraisingStates: string[];
    revenue: string;
    assets: string;
    fundraising?: string[];
    hasEmployees?: string[];
    isChurch?: string | null;
    usesProfessionalFundraiser?: string;
    conductRaffles?: string;
    format: string;
  };
  resultSummary: {
    total: number;
    federal: number;
    solicitation: number;
    corporate: number;
    tax: number;
    fundraising: number;
    employment: number;
  };
  responseTimeMs: number;
}

export interface AssessmentPage {
  data: Assessment[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface Stats {
  totals: { totalRequests: number; avgResponseMs: number; avgFilings: number };
  byKey: Array<{ _id: string; label: string; requests: number; lastUsed: string }>;
}

/** Coerce admin /stats JSON into a safe shape (handles proxies or partial responses). */
export function normalizeStatsPayload(raw: unknown): Stats {
  if (!raw || typeof raw !== 'object') {
    return { totals: { totalRequests: 0, avgResponseMs: 0, avgFilings: 0 }, byKey: [] };
  }
  const r = raw as Partial<Stats>;
  const t = r.totals;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);
  const rows: Stats['byKey'] = [];
  if (Array.isArray(r.byKey)) {
    r.byKey.forEach((row, i) => {
      if (!row || typeof row !== 'object') return;
      const o = row as Partial<Stats['byKey'][number]>;
      rows.push({
        _id: o._id != null ? String(o._id) : String(i),
        label: o.label != null ? String(o.label) : '—',
        requests: n(o.requests),
        lastUsed: o.lastUsed != null ? String(o.lastUsed) : '',
      });
    });
  }
  return {
    totals: {
      totalRequests: n(t?.totalRequests),
      avgResponseMs: n(t?.avgResponseMs),
      avgFilings: n(t?.avgFilings),
    },
    byKey: rows,
  };
}

export interface TimeseriesPoint {
  bucket: string;
  requests: number;
  avgFilings: number;
  avgResponseMs: number;
}

export const assessmentsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    keyId?: string;
    from?: string;
    to?: string;
  }): Promise<AssessmentPage> => base.get('/admin/assessments', { params }).then((r) => r.data),
  stats: (): Promise<Stats> => base.get('/admin/assessments/stats').then((r) => r.data),
  get: (id: string): Promise<Assessment> =>
    base.get(`/admin/assessments/${id}`).then((r) => r.data),
  timeseries: (params?: { bucket?: string; from?: string; to?: string }): Promise<{ bucket: string; points: TimeseriesPoint[] }> =>
    base.get('/admin/assessments/timeseries', { params }).then((r) => r.data),
  replay: (id: string): Promise<CompliancePreviewResponse & { sourceAssessmentId: string }> =>
    base.post(`/admin/compliance/preview/from-assessment/${id}`).then((r) => r.data),
};

// ─── Compliance preview / explain / diff / calendar / export ────────────────

export interface FilingRequirement {
  category:
    | 'federal'
    | 'state-solicitation'
    | 'state-corporate'
    | 'state-tax'
    | 'fundraising'
    | 'employment'
    | 'certificate-of-authority'
    | 'audit';
  title: string;
  description: string;
  state?: string;
  deadline?: string;
  fee?: string;
  priority: 'required' | 'likely' | 'conditional' | 'recommended';
  actionUrl?: string;
  actionLabel?: string;
  reasons?: string[];
}

export type FilingStatus = 'current' | 'pastDue' | 'revoked';

export interface CompliancePreviewRequest {
  entity: string;
  states: string[];
  fundraisingStates: string[];
  revenue: string;
  assets: string;
  fundraising: string[];
  hasEmployees: string[];
  isChurch?: string | null;
  usesProfessionalFundraiser?: string;
  conductRaffles?: string;
  /** For 501(c)(3)s. Routes the annual return between the 990 series and 990-PF. */
  foundationStatus?: 'publicCharity' | 'privateFoundation' | null;
  /** First-ever filing vs. an ongoing renewal. */
  filingType?: 'initial' | 'renewal' | null;
  /** Standing per agency; omitted agencies are assumed current. */
  filingStatus?: {
    charitableSolicitation?: FilingStatus;
    annualReport?: FilingStatus;
    federal?: FilingStatus;
  } | null;
  /** State of incorporation, for certificate-of-authority requirements. */
  domesticState?: string | null;
  /** Exact gross revenue; the revenue buckets are too coarse for audit thresholds. */
  annualRevenueAmount?: number | null;
}

export interface CompliancePreviewResponse {
  orgTypeName: string;
  date: string;
  summary: string;
  revenueLabel: string;
  assetsLabel: string;
  filings: FilingRequirement[];
  counts: {
    federal: number;
    solicitation: number;
    corporate: number;
    tax: number;
    fundraising: number;
    employment: number;
    certificateOfAuthority: number;
    audit: number;
    total: number;
  };
  responseTimeMs: number;
}

export interface ReportDiff {
  added: FilingRequirement[];
  removed: FilingRequirement[];
  unchanged: number;
  countsBefore: CompliancePreviewResponse['counts'];
  countsAfter: CompliancePreviewResponse['counts'];
}

export interface CalendarEntry {
  title: string;
  date: string;
  description: string;
  category: FilingRequirement['category'];
  state?: string;
  priority: FilingRequirement['priority'];
}

export const complianceApi = {
  preview: (body: CompliancePreviewRequest, opts?: { explain?: boolean }): Promise<CompliancePreviewResponse> =>
    base
      .post('/admin/compliance/preview', body, { params: opts?.explain ? { explain: 'true' } : {} })
      .then((r) => r.data),
  diff: async (
    before: CompliancePreviewRequest,
    after: CompliancePreviewRequest
  ): Promise<ReportDiff> => {
    const [b, a] = await Promise.all([
      base.post('/admin/compliance/preview', before).then((r) => r.data),
      base.post('/admin/compliance/preview', after).then((r) => r.data),
    ]);
    return diffLocal(b, a);
  },
};

function filingKey(f: FilingRequirement): string {
  return `${f.category}|${f.state ?? ''}|${f.title}`;
}

function diffLocal(before: CompliancePreviewResponse, after: CompliancePreviewResponse): ReportDiff {
  const beforeMap = new Map(before.filings.map((f) => [filingKey(f), f]));
  const afterMap = new Map(after.filings.map((f) => [filingKey(f), f]));
  const added: FilingRequirement[] = [];
  const removed: FilingRequirement[] = [];
  for (const [k, f] of afterMap) if (!beforeMap.has(k)) added.push(f);
  for (const [k, f] of beforeMap) if (!afterMap.has(k)) removed.push(f);
  return {
    added,
    removed,
    unchanged: before.filings.length - removed.length,
    countsBefore: before.counts,
    countsAfter: after.counts,
  };
}

// ─── Deadline rules engine ──────────────────────────────────────────────────

import type { DeadlineRule } from './deadlineRules';

export interface ComplianceProfile {
  jurisdiction?: string;
  entityKey?: string;
  formationDate?: string;
  registrationDate?: string;
  fiscalYearEnd?: { month: number; day: number };
  annualRevenue?: number;
  totalAssets?: number;
  hasEmployees?: boolean;
  isChurch?: boolean;
  solicitsIn?: string[];
  flags?: Record<string, boolean>;
  /** Formation-event anchors for initial-filing rules. */
  incorporationDate?: string;
  irsSubmissionDate?: string;
  irsDeterminationLetterDate?: string;
  /** Routes the annual return between the 990 series and the 990-PF. */
  foundationStatus?: 'publicCharity' | 'privateFoundation';
  /** First-ever filing vs. an ongoing renewal. */
  filingType?: 'initial' | 'renewal';
  /** Standing per agency; omitted agencies are treated as current. */
  filingStatus?: {
    charitableSolicitation?: FilingStatus;
    annualReport?: FilingStatus;
    federal?: FilingStatus;
  };
}

export interface ProcessingTime {
  minDays?: number;
  maxDays?: number;
  businessDays?: boolean;
  notes?: string;
}

export interface DeadlineHorizon {
  from: string;
  to: string;
}

export interface DeadlineOccurrence {
  source?: {
    collection: string;
    recordId?: string;
    ruleIndex: number;
    groupId?: string;
  };
  jurisdiction?: string;
  label: string;
  dueDate: string | null;
  effectiveDueDate?: string | null;
  /** End of the final extension window; each runs from the end of the previous. */
  extendedDueDate?: string | null;
  /** End date of each extension window, in order. */
  extendedDueDates?: string[];
  /** When the filing window opens — the deadline says when it is late. */
  windowOpensDate?: string | null;
  processingTime?: ProcessingTime;
  qualitativeText?: string;
  recurrence: { kind: string; parity?: string };
  /** First extension. Retained for callers written against the single form. */
  extension?: {
    form?: string;
    automatic?: boolean;
    description?: string;
    offset: { months?: number; days?: number; weeks?: number };
  };
  /** Every extension window in order, for filings that grant more than one. */
  extensions?: Array<{
    form?: string;
    automatic?: boolean;
    description?: string;
    offset: { months?: number; days?: number; weeks?: number };
  }>;
  rule: DeadlineRule;
}

export interface DeadlineComputeResponse {
  profile: ComplianceProfile;
  horizon: DeadlineHorizon;
  occurrences: DeadlineOccurrence[];
  totals: { rulesEvaluated: number; occurrences: number };
}

export interface DeadlinePreviewResponse extends DeadlineComputeResponse {
  issues: string[][];
}

export const deadlinesApi = {
  compute: (
    profile: ComplianceProfile,
    horizon: DeadlineHorizon,
    options?: {
      skipFederal?: boolean;
      skipCharitable?: boolean;
      skipAnnualReports?: boolean;
      clipToHorizon?: boolean;
      excludeQualitative?: boolean;
    }
  ): Promise<DeadlineComputeResponse> =>
    base
      .post('/admin/deadlines/compute', { profile, horizon, options })
      .then((r) => r.data),

  preview: (
    rules: DeadlineRule[],
    profile: ComplianceProfile,
    horizon: DeadlineHorizon,
    extras?: { frequency?: string; clipToHorizon?: boolean; excludeQualitative?: boolean }
  ): Promise<DeadlinePreviewResponse> =>
    base
      .post('/admin/deadlines/preview', { rules, profile, horizon, ...(extras ?? {}) })
      .then((r) => r.data),
};

// ─── Health / version ────────────────────────────────────────────────────────

export interface DbHealth {
  status: 'ok' | 'error';
  /** Present on current API; may be missing from older or proxied responses. */
  ping?: { ok: boolean; ms: number | null; error: string | null };
  connectionState: string;
  host?: string;
  name?: string;
  collections: Record<string, number>;
}

export interface VersionInfo {
  apiVersion: string;
  dataVersion: string | null;
  lastUpdated: string | null;
}

export const healthApi = {
  db: (): Promise<DbHealth> => base.get('/admin/health/db').then((r) => r.data),
  version: (): Promise<VersionInfo> => base.get('/api/version').then((r) => r.data),
};

// ─── Audit log ───────────────────────────────────────────────────────────────

export interface AuditEntry {
  _id: string;
  at: string;
  actor?: { sub?: string; email?: string };
  action: string;
  resource: string;
  resourceId?: string;
  method?: string;
  path?: string;
  statusCode?: number;
  diff?: unknown;
}

export interface AuditPage {
  data: AuditEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const auditApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    resource?: string;
    actor?: string;
    from?: string;
    to?: string;
  }): Promise<AuditPage> => base.get('/admin/audit', { params }).then((r) => r.data),
};

// ─── Validation tools ────────────────────────────────────────────────────────

export interface StateValidationDataset {
  count: number;
  missing: string[];
  unknown: string[];
  duplicates: Array<{ abbr: string; count: number }>;
}

export type StateValidationReport = Record<string, StateValidationDataset>;

export interface DeadlineRuleIssue {
  state: string;
  abbreviation: string;
  issues: string[];
}

export const validationApi = {
  states: (): Promise<StateValidationReport> =>
    base.get('/admin/validate/states').then((r) => r.data),
  deadlineRules: (): Promise<{
    totalChecked: number;
    withIssues: number;
    issues: DeadlineRuleIssue[];
  }> => base.get('/admin/validate/deadline-rules').then((r) => r.data),
};

// ─── Changelog (admin endpoint, surfaces ref-data edits) ─────────────────────

export interface ChangelogEntry {
  _id: string;
  at: string;
  resource: string;
  resourceId?: string;
  action: 'create' | 'update' | 'delete';
  actor?: { sub?: string; email?: string };
  summary?: string;
}

export interface ChangelogPage {
  data: ChangelogEntry[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const changelogApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    resource?: string;
    since?: string;
  }): Promise<ChangelogPage> => base.get('/admin/changelog', { params }).then((r) => r.data),
};

// ─── Saved assessments ───────────────────────────────────────────────────────

export interface SavedAssessment {
  _id: string;
  apiKeyId: string;
  apiKeyLabel?: string;
  name: string;
  notes?: string;
  params: CompliancePreviewRequest;
  fiscalYearEnd?: { month: number; day: number };
  createdAt: string;
  updatedAt: string;
}

export const savedAssessmentsApi = {
  list: (keyId?: string): Promise<SavedAssessment[]> =>
    base.get('/admin/saved-assessments', { params: keyId ? { keyId } : {} }).then((r) => r.data),
  get: (id: string): Promise<SavedAssessment> =>
    base.get(`/admin/saved-assessments/${id}`).then((r) => r.data),
  remove: (id: string): Promise<void> =>
    base.delete(`/admin/saved-assessments/${id}`).then(() => undefined),
};

// ─── Generic CRUD client (now supports pagination + search) ──────────────────

export interface CrudDoc {
  _id: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface CrudListParams {
  page?: number;
  limit?: number;
  q?: string;
  sort?: string;
  // additional field=value filters
  [key: string]: string | number | undefined;
}

export interface CrudClient<T extends CrudDoc> {
  list: () => Promise<T[]>;
  listPaginated: (params: CrudListParams) => Promise<PaginatedResponse<T>>;
  get: (id: string) => Promise<T>;
  create: (body: Partial<T>) => Promise<T>;
  update: (id: string, body: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<void>;
  bulkUpsert: (items: Partial<T>[]) => Promise<{
    matched?: number;
    modified?: number;
    upserted?: number;
    inserted?: number;
  }>;
  exportUrl: () => string;
}

export function crudClient<T extends CrudDoc>(path: string): CrudClient<T> {
  const apiUrl = creedApiBaseUrl;
  return {
    list: () => base.get(path).then((r) => r.data),
    listPaginated: (params) =>
      base.get(path, { params: { page: 1, limit: 25, ...params } }).then((r) => r.data),
    get: (id) => base.get(`${path}/${id}`).then((r) => r.data),
    create: (body) => base.post(path, body).then((r) => r.data),
    update: (id, body) => base.put(`${path}/${id}`, body).then((r) => r.data),
    remove: (id) => base.delete(`${path}/${id}`).then(() => undefined),
    bulkUpsert: (items) => base.post(`${path}/bulk`, items).then((r) => r.data),
    exportUrl: () => `${apiUrl}${path}/export.json`,
  };
}
